import Table from "@app/components/table";
import { ColumnConfig, Order } from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import VoucherActiveModal from "@app/components/voucher-active-modal";
import {
  DATE_TIME_FORMAT_2,
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  SystemMessage,
} from "@app/constants";
import VoucherActiveService from "@app/services/http/voucher-active.service";
import { openPortalDialog } from "@app/services/modal.service";
import { CreateVoucherActive, Meta, VoucherActive } from "@app/types";
import { convertDate, formatDateTime } from "@app/utils/util";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import { useEffect, useState } from "react";

function ExpiredTimeColumnTable({ data }: { data: VoucherActive }) {
  return <div>{formatDateTime(data.expiredTime)}</div>;
}

function CreatedDateColumnTable({ data }: { data: VoucherActive }) {
  return <div>{formatDateTime(data.createdDate)}</div>;
}

const columnConfig: ColumnConfig[] = [
  {
    label: "Mã",
    dataKey: "code",
    width: "20%",
    tdClassName: "truncate min-w-[150px] max-w-[250px]",
    hasOrder: true,
  },
  {
    label: "Tên",
    dataKey: "name",
    width: "20%",
    hasOrder: true,
  },
  {
    label: "Thời gian hiệu lực",
    dataKey: "expiredTime",
    width: "20%",
    component: ExpiredTimeColumnTable,
    hasOrder: true,
  },
  {
    label: "Số người dùng tối đa",
    dataKey: "totalUse",
    width: "20%",
    hasOrder: true,
  },
  {
    label: "Ngày tạo",
    dataKey: "createdDate",
    width: "20%",
    component: CreatedDateColumnTable,
    hasOrder: true,
  },
];

function VoucherActiveManagement() {
  const [voucherActives, setVoucherActives] = useState<VoucherActive[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const [update, forceUpdate] = useForceUpdate();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    VoucherActiveService.getList(requestParams, abortController.signal).then(
      (res) => {
        setVoucherActives(res.data.items);
        setMeta(res.data.meta);

        if (
          requestParams.pageNum &&
          res.data.meta.totalPage > 0 &&
          res.data.meta.totalPage < requestParams.pageNum
        ) {
          const newRequestParams: RequestParams = {
            ...requestParams,
            pageNum: DEFAULT_PAGE,
          };

          setRequestParams(newRequestParams);
        }
      }
    );
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateClick = () => {
    const modalObs = openPortalDialog(VoucherActiveModal);

    modalObs.afterClosed().subscribe((data) => {
      if (data?.voucherActive) {
        const voucherActive: CreateVoucherActive = {
          code: data.voucherActive.code,
          name: data.voucherActive.name,
          expiredTime:
            convertDate(
              data.voucherActive.expiredTime,
              DATE_TIME_FORMAT_2
            ).getTime() / 1000,
          totalUse: Number(data.voucherActive.totalUse),
        };

        VoucherActiveService.create(voucherActive).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleEditClick = (voucherActive: VoucherActive) => {
    const modalObs = openPortalDialog(VoucherActiveModal, {
      isEdit: true,
      voucherActive,
    });

    modalObs.afterClosed().subscribe((data) => {
      if (data?.voucherActive) {
        const updateVoucherActive: CreateVoucherActive = {
          code: data.voucherActive.code,
          name: data.voucherActive.name,
          expiredTime:
            convertDate(
              data.voucherActive.expiredTime,
              DATE_TIME_FORMAT_2
            ).getTime() / 1000,
          totalUse: Number(data.voucherActive.totalUse),
        };

        VoucherActiveService.update(voucherActive.id, updateVoucherActive).then(
          () => {
            addToast({ text: SystemMessage.UPDATE_SUCCESSFULLY });
            forceUpdate();
          }
        );
      }
    });
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách voucher active</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách voucher active"
          onInputChange={onSearchChange}
        />
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={voucherActives}
            hasAction2
            onEditClick={handleEditClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default VoucherActiveManagement;
